import { useHistory } from 'react-router-dom';
import { classNames, React } from 'libs/react';
import { useEffect, useIdent, useMemo, useRef, useState } from 'libs/react';
import { useDebounce } from 'libs/react/hook/useDebounce';
import { useQuery } from 'libs/react';
import { Icon } from 'components/Icon';
import { Spinner } from 'components/Spinner';
import { Text } from 'components/Text';
import { TextInput } from 'components/form/private/inputs/TextInput';
import { useRoute } from 'components/router/Routing';
import { useTranslation } from 'components/translation/TranslationProvider';
import { GlobalSearchQuery } from 'graphql/Search/queries/GlobalSearchQuery';
import { SearchResultItem } from 'modules/search/components/SearchResultItem';
import { clearRecentSearches, readRecentSearches, rememberSearch } from 'modules/search/recentSearches';
import { SEARCH_ENTITY_ORDER, flattenGroups, searchEntityLabel, searchEntityPicto, searchEntityRoute } from 'modules/search/searchEntities';
import './GlobalSearchField.scss';
/**
 * Shortest term that triggers a request. Below this the index cannot match
 * anything useful and every keystroke would be a wasted round trip.
 *
 * @const MIN_TERM_LENGTH
 * @hidden
 */
const MIN_TERM_LENGTH = 2;
/**
 * Items per group in the preview. Shared with the results screen so a
 * navigation from one to the other is served from the Apollo cache.
 *
 * @const PREVIEW_LIMIT
 * @hidden
 */
const PREVIEW_LIMIT = 5;
/**
 * @const DEBOUNCE_MS
 * @hidden
 */
const DEBOUNCE_MS = 250;
/**
 * The global search field and its preview, rendered in the centre section of
 * the navigation bar.
 *
 * It holds no opinion about where it may appear: NavigationBar asks
 * useGlobalSearchEnabled() and simply does not render it when the answer is
 * no. Nor does it know about dark backgrounds — inside the Header it inherits
 * --screen-background-color, and its own colours are CSS variables a screen
 * can redefine.
 *
 * @component GlobalSearchField
 * @since 1.0.0
 */
export function GlobalSearchField() {
    var _a, _b, _c, _d;
    const route = useRoute();
    const history = useHistory();
    const { customLabels, language } = useTranslation();
    const [ident] = useIdent('global-search');
    const containerRef = useRef(null);
    // Two states: `value` follows every keystroke so the field stays
    // controlled, `term` lags behind it and is what the query reads.
    const [value, setValue] = useState('');
    const [term, setTerm] = useState('');
    const [opened, setOpened] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [recent, setRecent] = useState(() => readRecentSearches());
    const commit = useDebounce((next) => {
        const trimmed = String(next !== null && next !== void 0 ? next : '').trim();
        setTerm(trimmed.length >= MIN_TERM_LENGTH ? trimmed : '');
    }, DEBOUNCE_MS);
    // Memoised, otherwise a new object every render retriggers the query forever.
    const input = useMemo(() => ({ search: term, limit: PREVIEW_LIMIT }), [term]);
    /*
     * Both `skip` and `fetchPolicy` are passed on purpose: the in-house hook
     * (libs/react/apollo) only rewrites `skip` when `fetchPolicy` is absent, and
     * that rewrite replaces it with a cache-only policy. Passing both keeps
     * `skip` with its standard Apollo meaning.
     */
    const [data, ready] = useQuery(GlobalSearchQuery, {
        variables: { input },
        skip: term === '',
        fetchPolicy: 'cache-first'
    });
    const groups = useMemo(() => {
        var _a, _b;
        return [...((_b = (_a = data === null || data === void 0 ? void 0 : data.globalSearch) === null || _a === void 0 ? void 0 : _a.groups) !== null && _b !== void 0 ? _b : [])].sort((left, right) => SEARCH_ENTITY_ORDER.indexOf(left.entity) -
            SEARCH_ENTITY_ORDER.indexOf(right.entity));
    }, [data]);
    // The keyboard walks one flat list, not the nested groups.
    const items = useMemo(() => flattenGroups(groups), [groups]);
    const total = (_b = (_a = data === null || data === void 0 ? void 0 : data.globalSearch) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0;
    const partial = (_d = (_c = data === null || data === void 0 ? void 0 : data.globalSearch) === null || _c === void 0 ? void 0 : _c.partial) !== null && _d !== void 0 ? _d : false;
    /*
     * Covers the debounce as well as the request: the moment a keystroke makes
     * `value` differ from the committed `term`, a search is on its way, and
     * waiting 250 ms before showing anything reads as a frozen field.
     */
    const trimmed = value.trim();
    const searching = trimmed.length >= MIN_TERM_LENGTH && (!ready || trimmed !== term);
    // A new term invalidates the previous highlight position.
    useEffect(() => {
        setHighlight(0);
    }, [term]);
    /*
     * Keeps the active row in view when the keyboard walks past the fold. The
     * dropdown scrolls, so arrowing down would otherwise move the selection out
     * of sight.
     */
    useEffect(() => {
        if (!opened) {
            return;
        }
        const active = document.getElementById(`${ident}-item-${highlight}`);
        if (active && active.scrollIntoView) {
            active.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight, opened, ident]);
    useEffect(() => {
        if (!opened) {
            return;
        }
        function handleClickOutside(e) {
            if (containerRef.current &&
                containerRef.current.contains(e.target)) {
                return;
            }
            setOpened(false);
        }
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [opened]);
    /**
     * @function close
     * @since 1.0.0
     * @hidden
     */
    function close() {
        setOpened(false);
        setHighlight(0);
    }
    /**
     * @function onRecent
     * @since 1.0.0
     * @hidden
     */
    function onRecent(entry) {
        setValue(entry);
        setTerm(entry);
        setOpened(true);
        setHighlight(0);
    }
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function onChange(next) {
        setValue(next);
        setOpened(true);
        commit(next);
    }
    /**
     * @function onClear
     * @since 1.0.0
     * @hidden
     */
    function onClear() {
        setValue('');
        setTerm('');
        close();
    }
    /**
     * Opens the full results screen.
     *
     * RouteManager only handles path parameters, so the path comes from
     * route.gen (never hardcoded) and the query string is appended here.
     *
     * @function openResults
     * @since 1.0.0
     * @hidden
     */
    function openResults() {
        const trimmed = value.trim();
        if (trimmed.length < MIN_TERM_LENGTH) {
            return;
        }
        setRecent(rememberSearch(trimmed));
        close();
        history.push(`${route.gen('search')}?q=${encodeURIComponent(trimmed)}`);
    }
    /**
     * @function onSelect
     * @since 1.0.0
     * @hidden
     */
    function onSelect(item) {
        // A term that led somewhere is worth remembering.
        setRecent(rememberSearch(value));
        close();
        /*
         * The documentation bucket has no route: SearchResultItem opens the
         * article in the documentation drawer. Anything else with no route is not
         * navigable, so the full screen is the honest fallback.
         */
        if (item.entity !== 'DOCUMENTATION' && !searchEntityRoute(item)) {
            openResults();
        }
    }
    /**
     * @function onKeyDown
     * @since 1.0.0
     * @hidden
     */
    function onKeyDown(e) {
        if (e.key === 'Escape') {
            close();
            e.currentTarget.blur();
            return;
        }
        if (e.key === 'Tab') {
            close();
            return;
        }
        if (e.key === 'Enter') {
            const item = items[highlight];
            if (opened && item) {
                onSelect(item);
                const target = searchEntityRoute(item);
                if (target) {
                    route.push(target);
                }
                return;
            }
            openResults();
            return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (items.length === 0) {
                return;
            }
            // Without this the caret jumps to either end of the field.
            e.preventDefault();
            setOpened(true);
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setHighlight((current) => {
                const next = current + step;
                if (next < 0) {
                    return items.length - 1;
                }
                if (next >= items.length) {
                    return 0;
                }
                return next;
            });
        }
    }
    const showRecent = opened &&
        term === '' &&
        !searching &&
        value.trim() === '' &&
        recent.length > 0;
    const showDropdown = opened && (term !== '' || searching || showRecent);
    const className = classNames('global-search-field', {
        'global-search-field--is-open': showDropdown
    });
    return (React.createElement("div", { className: className, ref: containerRef },
        React.createElement("div", { className: "global-search-field-input", role: "combobox", "aria-expanded": showDropdown, "aria-haspopup": "listbox", "aria-owns": `${ident}-list`, "aria-activedescendant": items.length > 0 && items[highlight]
                ? `${ident}-item-${highlight}`
                : undefined },
            React.createElement(Icon, { className: "global-search-field-picto", value: "fal search" }),
            React.createElement(TextInput, { ident: ident, name: "globalSearch", value: value, placeholder: t('Search companies, quotes, help…'), autocomplete: false, autocorrect: false, onChange: onChange, onFocus: () => setOpened(true), onKeyDown: onKeyDown }),
            value !== '' && (React.createElement("div", { className: "global-search-field-clear", onClick: onClear },
                React.createElement(Icon, { value: "fal times" })))),
        showDropdown && (React.createElement("div", { className: "global-search-field-dropdown", id: `${ident}-list`, role: "listbox" },
            showRecent && (React.createElement("div", { className: "global-search-field-recent" },
                React.createElement("div", { className: "global-search-field-group-head" },
                    React.createElement(Icon, { className: "global-search-field-group-picto", value: "fal clock-rotate-left" }),
                    React.createElement(Text, { className: "global-search-field-group-title", value: t('Recent searches') }),
                    React.createElement(Text, { className: "global-search-field-recent-clear", value: t('Clear'), onClick: () => setRecent(clearRecentSearches()) })),
                recent.map((entry) => (React.createElement("div", { className: "global-search-field-recent-item", key: entry, onClick: () => onRecent(entry) },
                    React.createElement(Text, { value: entry })))))),
            searching && (React.createElement("div", { className: "global-search-field-loading" },
                React.createElement(Spinner, { style: "small" }))),
            !searching && !showRecent && groups.length === 0 && (React.createElement("div", { className: "global-search-field-empty" },
                React.createElement(Text, { value: partial
                        ? t('Type at least three characters to search the text.')
                        : t('No result.') }))),
            !searching &&
                !showRecent &&
                groups.map((group) => {
                    var _a, _b;
                    return (React.createElement("div", { className: "global-search-field-group", key: group.entity },
                        React.createElement("div", { className: "global-search-field-group-head" },
                            React.createElement(Icon, { className: "global-search-field-group-picto", value: (_a = searchEntityPicto(group.entity)) !== null && _a !== void 0 ? _a : 'fal circle' }),
                            React.createElement(Text, { className: "global-search-field-group-title", value: `${searchEntityLabel(group.entity, customLabels, language)} (${group.count})` })),
                        ((_b = group.items) !== null && _b !== void 0 ? _b : []).map((item) => (React.createElement(SearchResultItem, { key: `${item.entity}-${item.uuid}`, ident: `${ident}-item-${items.indexOf(item)}`, item: item, term: term, highlighted: items[highlight] === item, onSelect: onSelect })))));
                }),
            !searching && total > 0 && (React.createElement("div", { className: "global-search-field-more", onClick: openResults },
                React.createElement(Text, { value: t('See all results').replace('{{count}}', String(total)) }),
                React.createElement(Text, { className: "global-search-field-more-count", value: String(total) })))))));
}
